import React, { useState, useEffect } from 'react';
import { Crown, Check, Sparkles, Clock, Calendar, Gift, ArrowRight, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { API } from '../config';

const FALLBACK_PLANS = [
  {
    id: 'essential', name: 'Essential', duration: '60 min', price: '99.99',
    description: 'Perfect for regular relaxation',
    features: ['One 60-minute massage per month', '10% off additional treatments', 'Priority booking access', 'Free birthday upgrade to 90 min', 'Rollover unused sessions (up to 2)', 'Exclusive member offers'],
    popular: false, color: 'from-sage-500 to-sage-600'
  },
  {
    id: 'premium', name: 'Premium', duration: '120 min', price: '114.99',
    description: 'Ultimate relaxation experience',
    features: ['One 120-minute massage per month', '15% off additional treatments', 'Priority booking access', 'Free birthday upgrade + gift', 'Rollover unused sessions (up to 3)', 'Exclusive member offers', 'Complimentary aromatherapy upgrade', 'Guest pass once per quarter'],
    popular: true, color: 'from-amber-500 to-amber-600'
  }
];

const WellnessClubSection = () => {
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plans, setPlans] = useState(FALLBACK_PLANS);

  useEffect(() => {
    fetch(`${API}/wellness-plans`)
      .then(res => res.json())
      .then(data => { if (data?.length) setPlans(data); })
      .catch(() => {});
  }, []);

  const handleJoin = (plan) => {
    setSelectedPlan(plan);
    setShowJoinModal(true);
  };

  return (
    <section id="wellness-club" className="py-28 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-amber-100/30 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-sage-100/30 rounded-full blur-3xl" />

      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 relative">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-luxury-gold text-xs uppercase tracking-[0.3em] font-medium">Exclusive Membership</span>
          
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-luxury-green mt-4 mb-6">
            Wellness Club
            <span className="block italic text-luxury-gold mt-1">Membership</span>
          </h2>
          
          <p className="text-stone-500 font-light">
            Join our exclusive wellness club and enjoy premium benefits and significant savings.
          </p>
        </div>

        {/* Membership Card Image */}
        <div className="flex justify-center mb-12">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-amber-600 rounded-2xl blur-xl opacity-30" />
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/83babd9b-e39b-4169-bd2b-2375b0301469/images/feb6f5f9c763922b880ac2649eb10db6f1afe444e891f044f40d11a09c34fb2b.png"
              alt="Wellness Club Card"
              className="relative w-64 h-auto rounded-2xl shadow-2xl"
            />
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white rounded-3xl p-8 shadow-xl border-2 transition-all duration-300 hover:shadow-2xl ${
                plan.popular ? 'border-amber-400' : 'border-stone-100'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-1.5 rounded-full text-sm font-medium flex items-center gap-2">
                    <Star size={14} className="fill-white" />
                    Most Popular
                  </div>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-semibold text-stone-800 mb-1">{plan.name}</h3>
                <p className="text-stone-500">{plan.description}</p>
              </div>

              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-stone-400 text-xl">€</span>
                  <span className={`text-5xl font-bold bg-gradient-to-r ${plan.color} bg-clip-text text-transparent`}>
                    {plan.price}
                  </span>
                  <span className="text-stone-400">/month</span>
                </div>
                <div className="flex items-center justify-center gap-2 mt-2 text-stone-600">
                  <Clock size={16} />
                  <span>{plan.duration} massage included</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3 text-stone-600">
                    <Check size={18} className={`mt-0.5 ${plan.popular ? 'text-amber-500' : 'text-sage-500'}`} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => handleJoin(plan)}
                className={`w-full py-6 rounded-full font-medium text-lg transition-all ${
                  plan.popular
                    ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-lg hover:shadow-xl'
                    : 'bg-stone-800 hover:bg-stone-900 text-white'
                }`}
                data-testid={`join-${plan.id}-btn`}
              >
                Join {plan.name}
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </div>
          ))}
        </div>

        {/* Benefits Row */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
          {[
            { icon: Calendar, text: 'Cancel Anytime' },
            { icon: Gift, text: 'No Join Fee' },
            { icon: Sparkles, text: 'Instant Benefits' },
            { icon: Crown, text: 'VIP Treatment' }
          ].map((item) => (
            <div key={item.text} className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-amber-50 rounded-full mb-3">
                <item.icon className="text-amber-600" size={20} />
              </div>
              <p className="text-stone-600 text-sm font-medium">{item.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Join Modal */}
      <Dialog open={showJoinModal} onOpenChange={setShowJoinModal}>
        <DialogContent className="max-w-md bg-white rounded-3xl p-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full mb-4">
              <Crown className="text-white" size={24} />
            </div>
            
            <DialogTitle className="text-2xl font-semibold text-stone-800 mb-2">
              Join {selectedPlan?.name} Plan
            </DialogTitle>
            
            <DialogDescription className="text-stone-500 mb-6">
              €{selectedPlan?.price}/month for {selectedPlan?.duration} massage
            </DialogDescription>

            <div className="bg-amber-50 rounded-xl p-6 mb-6 border border-amber-100">
              <p className="text-amber-700 font-medium mb-2">What happens next?</p>
              <ul className="text-left text-stone-600 text-sm space-y-2">
                <li className="flex items-center gap-2">
                  <Check size={14} className="text-amber-500" />
                  We'll contact you to set up your membership
                </li>
                <li className="flex items-center gap-2">
                  <Check size={14} className="text-amber-500" />
                  First payment taken when you book your first session
                </li>
                <li className="flex items-center gap-2">
                  <Check size={14} className="text-amber-500" />
                  Start enjoying benefits immediately
                </li>
              </ul>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => window.open(`https://wa.me/353857747474?text=Hello, I would like to join the Wellness Club ${selectedPlan?.name} Plan (€${selectedPlan?.price}/month for ${selectedPlan?.duration})`, '_blank')}
                className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-5 rounded-full font-medium"
                data-testid="join-whatsapp-btn"
              >
                Join via WhatsApp
              </Button>
              <Button
                onClick={() => setShowJoinModal(false)}
                variant="outline"
                className="flex-1 border-stone-300 text-stone-600 hover:bg-stone-50 py-5 rounded-full"
              >
                Maybe Later
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default WellnessClubSection;
